/**
 * 提示词创建/编辑表单组件
 */

import React, { useState, useEffect } from 'react';
import { Prompt, PromptInput, PromptService } from '../../services/prompts';
import { PROMPT_CATEGORIES } from '../../utils/categories';

interface PromptFormProps {
  prompt?: Prompt;
  isEditing?: boolean;
  onSubmit: (promptInput: PromptInput) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const PromptForm: React.FC<PromptFormProps> = ({
  prompt,
  isEditing = false,
  onSubmit,
  onCancel,
  loading = false
}) => {
  const [formData, setFormData] = useState<PromptInput>({
    title: '',
    content: '',
    description: '',
    tags: '',
    category: isEditing ? '' : '内容创作', // 新创建时默认为内容创作
    isFavorite: false,
  });

  const [errors, setErrors] = useState<string[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  // 初始化表单数据
  useEffect(() => {
    if (prompt) {
      setFormData({
        title: prompt.title || '',
        content: prompt.content || '',
        description: prompt.description || '',
        tags: PromptService.formatTagsToString(prompt.tags),
        category: prompt.category || '',
        isFavorite: prompt.isFavorite || false,
      });
    }
  }, [prompt]);

  // 处理表单字段变化
  const handleChange = (field: keyof PromptInput, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // 清除错误信息
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  // 处理创建并收藏
  const handleCreateAndFavorite = async () => {
    // 验证输入
    const validation = PromptService.validatePromptInput(formData);
    if (!validation.valid) {
      setErrors(validation.errors);
      return;
    }

    try {
      // 设置收藏状态并提交
      const favoriteData = { ...formData, isFavorite: true };
      await onSubmit(favoriteData);
    } catch (error) {
      if (error instanceof Error) {
        setErrors([error.message]);
      } else {
        setErrors(['提交失败，请稍后重试']);
      }
    }
  };

  // 处理表单提交
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 验证输入
    const validation = PromptService.validatePromptInput(formData);
    if (!validation.valid) {
      setErrors(validation.errors);
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      if (error instanceof Error) {
        setErrors([error.message]);
      } else {
        setErrors(['提交失败，请稍后重试']);
      }
    }
  };

  // 字符计数辅助函数
  const getCharCount = (text: string | undefined) => text?.length || 0;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">
          {isEditing ? '编辑提示词' : '创建新提示词'}
        </h3>
      </div>

      <form onSubmit={handleSubmit} className="p-6">
        {/* 错误信息显示 */}
        {errors.length > 0 && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <ul className="text-sm text-red-600 space-y-1">
              {errors.map((error, index) => (
                <li key={index}>• {error}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="space-y-6">
          {/* 标题字段 */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              标题 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="title"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="请输入提示词标题..."
              maxLength={200}
              disabled={loading}
            />
            <div className="mt-1 text-xs text-gray-500 text-right">
              {getCharCount(formData.title)}/200
            </div>
          </div>

          {/* 内容字段 */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <label htmlFor="content" className="block text-sm font-medium text-gray-700">
                提示词内容 <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={() => setShowPreview(!showPreview)}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                {showPreview ? '编辑模式' : '预览模式'}
              </button>
            </div>
            
            {showPreview ? (
              <div className="w-full min-h-[200px] px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                <div className="prose prose-sm max-w-none">
                  {formData.content ? (
                    <pre className="whitespace-pre-wrap font-sans text-sm text-gray-900">
                      {formData.content}
                    </pre>
                  ) : (
                    <p className="text-gray-400 italic">内容预览将显示在这里...</p>
                  )}
                </div>
              </div>
            ) : (
              <textarea
                id="content"
                value={formData.content}
                onChange={(e) => handleChange('content', e.target.value)}
                className="w-full min-h-[200px] px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical"
                placeholder="请输入提示词内容..."
                maxLength={10000}
                disabled={loading}
                required
              />
            )}
            <div className="mt-1 text-xs text-gray-500 text-right">
              {getCharCount(formData.content)}/10000
            </div>
          </div>

          {/* 描述字段 */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              描述说明 <span className="text-gray-400">(可选)</span>
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical"
              placeholder="请输入提示词的描述信息..."
              rows={3}
              maxLength={1000}
              disabled={loading}
            />
            <div className="mt-1 text-xs text-gray-500 text-right">
              {getCharCount(formData.description)}/1000
            </div>
          </div>

          {/* 分类和标签 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 分类字段 - 单选下拉 */}
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                业务分类 <span className="text-red-500">*</span>
              </label>
              <select
                id="category"
                value={formData.category}
                onChange={(e) => handleChange('category', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={loading}
              >
                <option value="">请选择分类</option>
                {PROMPT_CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.icon} {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* 标签字段 - 自由输入 */}
            <div>
              <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-1">
                功能标签 <span className="text-gray-400">(用逗号分隔)</span>
              </label>
              <input
                type="text"
                id="tags"
                value={formData.tags}
                onChange={(e) => handleChange('tags', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="例如：AI助手, 文案写作, 头脑风暴"
                disabled={loading}
              />
              <p className="mt-1 text-xs text-gray-500">
                用于精确搜索和筛选，可添加多个标签描述功能特征
              </p>
            </div>
          </div>
        </div>

        {/* 表单操作按钮 */}
        <div className="mt-8 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            取消
          </button>
          
          {!isEditing && (
            <button
              type="button"
              onClick={handleCreateAndFavorite}
              disabled={loading || formData.content.trim().length === 0 || (formData.title?.trim() || '').length === 0 || (formData.category?.trim() || '').length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 border border-transparent rounded-md shadow-sm hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  保存中...
                </div>
              ) : (
                '⭐ 创建并收藏'
              )}
            </button>
          )}
          
          <button
            type="submit"
            disabled={loading || formData.content.trim().length === 0 || (formData.title?.trim() || '').length === 0 || (formData.category?.trim() || '').length === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                保存中...
              </div>
            ) : (
              isEditing ? '更新提示词' : '创建提示词'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PromptForm; 